import {
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Button,
  Chip,
  alpha,
  useTheme,
  Skeleton,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useContext, useMemo } from "react";
import { useQuery } from "react-query";
import AuthContext from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { IconButton } from "@mui/material";
import { useSnackbar } from "notistack";

/**
 * AccountSwitcher Component
 *
 * Displays all active tenant sessions and allows users to:
 * - View all logged-in tenant accounts
 * - Switch between tenant accounts quickly
 * - See which account is currently active
 * - Add new tenant accounts
 */
function AccountSwitcher({ onClose, phone }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    tenantSessions,
    currentTenant,
    switchToTenant,
    removeTenantSession,
    clearAllTenantSessions,
    setAuth,
    clearTenantContext,
  } = useContext(AuthContext);

  const { data: tenantsResponse, isFetching } = useQuery(
    ["user-tenants-switcher", phone],
    () => axios.get(`/users/tenants?phone=${phone}`),
    {
      enabled: !!phone,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );
  const userTenants = useMemo(
    () => tenantsResponse?.data?.data || [],
    [tenantsResponse]
  );

  // Create a map of tenant IDs to tenant details for quick lookup
  const tenantDetailsMap = useMemo(() => {
    const map = {};
    userTenants.forEach((item) => {
      if (item?.tenant) {
        map[item.tenant.id] = item.tenant;
      }
    });
    return map;
  }, [userTenants]);

  // Handle switching to a different tenant
  const handleSwitchTenant = async (tenantId) => {
    if (tenantId === currentTenant?.id) {
      if (onClose) onClose();
      return;
    }

    try {
      await switchToTenant(tenantId, false);
      if (onClose) onClose();
      window.location.reload();
    } catch (error) {
      console.error("Error switching tenant:", error);
    }
  };

  // Handle adding a new account (tenant)
  const handleAddAccount = () => {
    if (onClose) onClose();
    // Navigate to login with prefilled phone and special multi-tenant-add flag
    navigate("/login", {
      state: {
        prefilledPhone: phone,
        skipPhoneInput: true,
        multiTenantAdd: true,
      },
    });
  };

  // Handle logout for a specific tenant session
  const handleLogoutTenant = async (session) => {
    const tenantId = session.tenant_id;
    const refreshToken = session?.auth?.refresh_token;

    try {
      if (refreshToken) {
        await axios.post(
          "/logout",
          { refresh_token: refreshToken },
          { withCredentials: true }
        );
      }
    } catch (error) {
      console.error("Error logging out tenant:", error);
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
          error?.message ||
          "Failed to logout from tenant",
        {
          variant: "error",
          autoHideDuration: 2500,
        }
      );
    }

    // Local cleanup
    removeTenantSession(tenantId);

    const remainingSessions = tenantSessions.filter(
      (s) => s.tenant_id !== tenantId
    );
    const isCurrent = currentTenant?.id === tenantId;

    // If we logged out the currently active tenant
    if (isCurrent) {
      if (remainingSessions.length > 0) {
        const next = remainingSessions[remainingSessions.length - 1];
        try {
          await switchToTenant(next.tenant_id, false);
          // Reload to ensure UI and data match the new tenant
          window.location.reload();
        } catch (error) {
          console.error("Error switching to next tenant after logout:", error);
        }
      } else {
        // No tenants left: clear auth and go to login
        clearAllTenantSessions();
        setAuth({});
        clearTenantContext(true);
        window.location.href = "/login";
      }
    }

    enqueueSnackbar("Logged out from tenant", {
      variant: "success",
      autoHideDuration: 2000,
    });
  };

  // If no sessions, show empty state
  if (!tenantSessions || tenantSessions.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No active tenant sessions
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddAccount}
          sx={{
            textTransform: "none",
            borderRadius: 2,
          }}
        >
          Add Tenant Account
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 280, maxWidth: 320, px: 2 }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: "text.secondary",
            textTransform: "uppercase",
            fontSize: "0.75rem",
            letterSpacing: 0.5,
          }}
        >
          Your Tenant Accounts
        </Typography>
        {tenantSessions.length > 1 && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.7rem",
            }}
          >
            {tenantSessions.length} accounts logged in
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Tenant Sessions List */}
      <List
        sx={{
          p: 0,
          maxHeight: tenantSessions.length > 4 ? 300 : "auto",
          overflowY: tenantSessions.length > 4 ? "auto" : "visible",
        }}
      >
        {isFetching
          ? // Loading skeleton
            [1, 2].map((item) => (
              <Box
                key={item}
                sx={{ py: 1.5, px: 2, display: "flex", alignItems: "center" }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ mr: 2 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={16}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            ))
          : tenantSessions.map((session, index) => {
              const isActive = session.tenant_id === currentTenant?.id;
              const hasValidToken = session.auth?.access_token;
              const tenantDetails = tenantDetailsMap[session.tenant_id];
              const tenantName =
                tenantDetails?.name || `Tenant ${session.tenant_id}`;
              const tenantLogo = tenantDetails?.logo;

              return (
                <ListItemButton
                  key={session.tenant_id}
                  onClick={() => handleSwitchTenant(session.tenant_id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    backgroundColor: isActive
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                    borderLeft: isActive
                      ? `3px solid ${theme.palette.primary.main}`
                      : "3px solid transparent",
                    "&:hover": {
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.action.hover, 0.04),
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={tenantLogo}
                      sx={{
                        bgcolor: isActive ? "primary.main" : "grey.400",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isActive ? 600 : 400,
                            fontSize: "0.875rem",
                          }}
                          noWrap
                        >
                          {tenantName}
                        </Typography>
                        {isActive && (
                          <CheckCircleIcon
                            sx={{
                              fontSize: 16,
                              color: "primary.main",
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {hasValidToken && (
                          <Chip
                            label="Active Session"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              mt: 0.5,
                              backgroundColor: alpha(
                                theme.palette.success.main,
                                0.1
                              ),
                              color: "success.main",
                              "& .MuiChip-label": {
                                px: 1,
                              },
                            }}
                          />
                        )}
                      </Box>
                    }
                  />

                  {/* Per-tenant logout icon */}
                  <Box
                    sx={{
                      ml: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLogoutTenant(session);
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                        },
                      }}
                    >
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItemButton>
              );
            })}
      </List>

      <Divider />

      {/* Add Account Button */}
      <Box sx={{ py: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddAccount}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 500,
            py: 1,
          }}
        >
          Add Another Tenant
        </Button>
      </Box>

      {/* Info Text */}
      <Box sx={{ pb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.7rem",
            display: "block",
            textAlign: "center",
          }}
        >
          Click on an account to switch
        </Typography>
      </Box>
    </Box>
  );
}

export default AccountSwitcher;
